import { Box, ButtonBase, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HistoryOrder } from "../../../services/orders/history";
import {
  getRowAmount,
  getRowSubtitle,
  getTypeLabel,
  STATUS_META,
  TYPE_META,
} from "./helpers";

interface HistoryRowProps {
  order: HistoryOrder;
}

export function HistoryRow({ order }: HistoryRowProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("history");
  const typeMeta = TYPE_META[order.type];
  const statusMeta = STATUS_META[order.status];
  const amount = getRowAmount(order);
  const subtitle = getRowSubtitle(order, t);
  const label = getTypeLabel(order.type, t);
  const isTerminalError = statusMeta.group === "error";
  const isProcessing = statusMeta.group === "processing";

  // SBP-платежи имеют свой мини-экран статуса в /pay-qr/:id,
  // где показывается получатель, кнопки «Оплатить» / «Отменить» / «Готово».
  // Ведём на него, чтобы не дублировать логику в history/detail.
  const isSbp = order.type === "SBP_PAYOUT" && order.id.startsWith("sbp-");
  const target = isSbp
    ? `/pay-qr/${order.id.slice("sbp-".length)}`
    : `/history/${order.id}`;

  return (
    <ButtonBase
      // Пробрасываем `from: 'history'`, чтобы PayQrPendingPage знал куда вернуть
      // юзера при нажатии «Готово» или кнопки «Назад».
      onClick={() =>
        isSbp ? navigate(target, { state: { from: "history" } }) : navigate(target)
      }
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        width: "100%",
        maxWidth: "100%",
        // `minWidth: 0` — обязателен для того, чтобы вложенные
        // flex-items c `whiteSpace: nowrap` не «раздули» строку
        // и не вытолкнули её за пределы карточки-плитки.
        minWidth: 0,
        padding: "12px 2px",
        borderRadius: "10px",
        textAlign: "left",
        boxSizing: "border-box",
        transition: "background 0.15s",
        "&:active": {
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.04)"
              : "rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <Box
          sx={(theme) => ({
            width: 38,
            height: 38,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            // На карточке-плитке нужно чуть больше контраста, иначе
            // кружок практически исчезает на фоне paper.
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(0, 0, 0, 0.05)",
            color: theme.palette.primary.main,
          })}
        >
          <Icon icon={typeMeta.icon} width={20} height={20} color="currentColor" />
        </Box>
        {isProcessing && (
          <StatusDot
            color={statusMeta.color}
            icon="solar:clock-circle-bold-duotone"
          />
        )}
        {isTerminalError && (
          <StatusDot
            color={statusMeta.color}
            icon="solar:close-circle-bold-duotone"
          />
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              fontSize: "11px",
              color: "text.secondary",
              marginTop: "3px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            color: amount.primaryColor ?? "text.primary",
            whiteSpace: "nowrap",
            lineHeight: 1.2,
          }}
        >
          {amount.primary}
        </Typography>
        {amount.secondary && (
          <Typography
            sx={{
              fontSize: "11px",
              color: "text.secondary",
              marginTop: "3px",
              whiteSpace: "nowrap",
            }}
          >
            {amount.secondary}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
}

function StatusDot({ color, icon }: { color: string; icon: string }) {
  return (
    <Box
      sx={(theme) => ({
        position: "absolute",
        right: -2,
        bottom: -2,
        width: 16,
        height: 16,
        borderRadius: "999px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Кружок статуса вырезаем «под цвет карточки», чтобы был чистый
        // borderless-эффект, как на iOS. Раньше был `background.default`
        // и на карточке-плитке он смотрелся как дырка.
        bgcolor: theme.palette.background.paper,
      })}
    >
      <Icon icon={icon} width={16} height={16} color={color} />
    </Box>
  );
}
